import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Color, fill, HIRE_WINDOW_ROUTE, Size, addCalendarDays, positionDayBefore, employmentPositionsPath, positionListProblem, positionsChanged, positionsOldestFirst, t, type ApprovalHandOffPlan, type EmploymentPositionDraft, type EmploymentPositionRow } from '@bms2/shared';
import { Link } from 'react-router-dom';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button, DatePicker, PhoneInput, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { PAGES_PATH, patch, post, put, useApi, useRows } from './api';
import { DuplicateModal, type DuplicateHit } from './DuplicateModal';
import { useDocumentToday } from '../../shell/ViewerZone';
import styles from './RecordPage.module.css';

/**
 * The two-step person and employment modal (piece 13, decisions 0138 and 0165).
 * 🔨 One component read from either end: opened from the Company page it locks
 * the company and searches-or-creates the person; opened from the Person page
 * it locks the person and searches-or-creates the company. 💰 Locked means
 * filled from the opening context and not editable inside it — a pre-filled half
 * that changed on step two would walk around the guard from the other end.
 */
export type EmploymentModalOrigin = 'company' | 'person';

/**
 * The employment a pencil opened, on the Person page's Employments tile or the
 * Company page's Contacts tile, holding what it holds today so the modal opens
 * loaded (piece 13; decision 0589). 🔨 The same three facts step two collects —
 * the title, the work email and the work phone — because an employment created
 * here and one corrected here must agree on what it is made of.
 *
 * 🔨 The two dates are here too, and a correction to them is what the product
 * owner asked for on 2026-09-20: a start typed wrong is a typo, not a second
 * job. Ending is the one thing the fields do not do, because ending deactivates
 * the login (decision 0165) and belongs behind a confirmation.
 */
export interface EmploymentEdit {
  employmentId: string;
  /**
   * The other end of the employment, for the subtitle: the company on the
   * Person page's row and the person on the Company page's, which is the row's
   * own label from either end (decision 0589).
   */
  otherEnd: string | null;
  title: string;
  workEmail: string;
  workPhone: string;
  workPhoneExtension: string;
  /** The employment's own first day, as a day. */
  startDate: string;
  /** Its last day where it has ended, and null while it is open. */
  endDate: string | null;
  /** 🔒 Decisions 6446 and 6660: the positions it has held, oldest first, each its title and its two days; the window edits them as one list. */
  positions?: readonly EmploymentPositionRow[];
}

export interface EmploymentModalProps {
  open: boolean;
  origin: EmploymentModalOrigin;
  /** The locked half: the company when opened from a Company page, the person otherwise. */
  companyId?: string | null;
  companyName?: string | null;
  personId?: string | null;
  personName?: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The person's name comes back too, so a picker the window was opened from can show who it picked (T311). */
  onCreated: (result: { personId: string; employmentId: string; personName?: string }) => void;
  openerId?: string | null;
  /** The employment a pencil opened; absent, the modal creates one in two steps. */
  editing?: EmploymentEdit | null;
  /** 🔒 Decision 5382: an Accepted employer suggestion opens the window with the suggested company picked where it is listed, and named above the picker. */
  companyHint?: string | null;
}

/** A row of the window's positions list, keyed for React apart from its id, which a new row has none of. */
interface PositionLine extends EmploymentPositionDraft {
  key: string;
}

/** The list the window opens on: the row's own positions, or one made of the title and days it carries where it carries none. */
function openingLines(editing: EmploymentEdit | null): PositionLine[] {
  if (!editing) return [];
  const rows = editing.positions && editing.positions.length > 0
    ? positionsOldestFirst(editing.positions)
    : [{ id: null, version: null, title: editing.title, startDate: editing.startDate, endDate: editing.endDate }];
  return rows.map((row, index) => ({ key: row.id ?? `opened-${index}`, id: row.id, version: row.version ?? null, title: row.title, startDate: row.startDate, endDate: row.endDate }));
}

interface DuplicateCandidate {
  tier: string;
  candidate: { id?: string; firstName?: string; lastName?: string };
}

/** A company the Person page's picker offers; our own is listed unselectable with a sentence beside it (decision 0165). */
interface CompanyOption {
  id: string;
  displayName: string;
  selectable: boolean;
  reason: string | null;
  /** Where our own Company's jobs are made: the Hire window (decision 3181). */
  hireRoute?: string | null;
}

export function EmploymentModal(props: EmploymentModalProps) {
  const { open, origin, companyId: lockedCompanyId = null, companyName, personId: lockedPersonId = null, personName, apiBaseUrl = '', onCancel, onCreated, openerId = null, editing = null, companyHint = null } = props;
  const [openStep, setStep] = useState<1 | 2>(1);
  // 🔨 The last day opens on the viewer's own day, read once through
  // useDocumentToday, never the machine's UTC calendar day (decision 0261; T529).
  const today = useDocumentToday();
  /** The existing person the duplicate modal's considered answer named; the locked half wins where there is one. */
  const [pickedPersonId, setPickedPersonId] = useState<string | null>(null);
  const personId = lockedPersonId ?? pickedPersonId;
  /**
   * 🔨 Opened from the Person page, step one picks the company instead of
   * typing a name: the person is the locked half. 💰 The Person page's modal
   * showed the Company page's name fields, disabled and empty, and no company
   * at all, so nothing could be saved from that end (found while building,
   * 2026-09-09).
   */
  const [pickedCompanyId, setPickedCompanyId] = useState<string | null>(null);
  const companies = useRows<CompanyOption>(open && origin === 'person' && !editing ? `${PAGES_PATH}/employment-modal/companies` : null, apiBaseUrl);
  const ownCompany = companies.state.kind === 'ready' ? (companies.state.data.find((row) => !row.selectable) ?? null) : null;
  // 🔒 Decision 5382: the suggested employer is picked once its row is listed; a person still chooses and nothing is written until Save.
  const hinted = companies.state.kind === 'ready' && companyHint ? (companies.state.data.find((row) => row.selectable && row.displayName.trim().toLowerCase() === companyHint.trim().toLowerCase()) ?? null) : null;
  const pickedOrHinted = pickedCompanyId ?? hinted?.id ?? null;
  const companyId = origin === 'company' ? lockedCompanyId : pickedOrHinted;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // 🔨 The edit opens loaded, and it is keyed to the row it edits by its caller, so a
  // second row opened after the first does not show the first one's draft (piece 13).
  const [title, setTitle] = useState(editing?.title ?? '');
  const [workEmail, setWorkEmail] = useState(editing?.workEmail ?? '');
  const [workPhone, setWorkPhone] = useState(editing?.workPhone ?? '');
  const [extension, setExtension] = useState(editing?.workPhoneExtension ?? '');
  /**
   * The employment's own two dates. 🔨 The last day is the one field with two
   * readings, and which one it has is the row's state, never a mode the person
   * picks: on an employment already ended it is a correction Save writes, and on
   * an open one it is the day End employment closes the job on.
   */
  const [lastDay, setLastDay] = useState(editing?.endDate ?? today);
  /** 🔒 Decision 6660: the positions as one list, oldest first; the latest's title is the one the person card shows. */
  const [opened] = useState<PositionLine[]>(() => openingLines(editing));
  const [lines, setLines] = useState<PositionLine[]>(opened);
  const added = useRef(0);
  const alreadyEnded = editing?.endDate !== null && editing?.endDate !== undefined;
  /**
   * 🔒 Decision 11740 (T3608): the approvals hand-off End asks for, read only while End is offered. Each department the job
   * approves for opens on the successor the chain gives, and a choice made here replaces it; End waits until each has one.
   */
  const handOff = useApi<ApprovalHandOffPlan>(open && editing && !alreadyEnded ? `${PAGES_PATH}/employments/${editing.employmentId}/approval-handoff` : null, apiBaseUrl);
  const handOffPlan = handOff.state.kind === 'ready' && Array.isArray(handOff.state.data?.departments) ? handOff.state.data : null;
  const [successorPicks, setSuccessorPicks] = useState<Record<string, string>>({});
  const successors = useMemo<Record<string, string>>(() => Object.fromEntries((handOffPlan?.departments ?? []).map((row) => [row.key, successorPicks[row.key] ?? row.suggestedEmploymentId ?? ''])), [handOffPlan, successorPicks]);
  const [candidates, setCandidates] = useState<readonly DuplicateCandidate[]>([]);
  /** The same matches as the duplicate modal draws them, each carrying why it matched. */
  const [hits, setHits] = useState<readonly DuplicateHit[]>([]);
  /** 🔨 The modal stands until the person answers; dismissing it is the effortless answer, never a default choice. */
  const [asking, setAsking] = useState(false);
  /**
   * The candidates the question has already been asked about. 🔒 Step two adds
   * the email and phone to step one's candidates and asks again only about one
   * step one did not show (decision 2210). A changed name, or the window
   * closing, starts the list afresh.
   */
  const askedAbout = useRef<{ name: string; ids: Set<string> }>({ name: '', ids: new Set() });
  useEffect(() => {
    if (!open) askedAbout.current = { name: '', ids: new Set() };
  }, [open]);
  const [existingEmploymentId, setExistingEmploymentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reopening starts at step one; a modal that remembers half a previous attempt
  // is how a row lands against the wrong company. A closed modal holds no step
  // and no banner: both are derived from `open` rather than written back on
  // close, the same shape the View as picker uses for its selection.
  const step: 1 | 2 = open ? openStep : 1;
  const shownError = open ? error : null;
  const shownCandidates = open ? candidates : [];
  const shownExisting = open ? existingEmploymentId : null;

  /**
   * 🔨 The duplicate check runs on both steps, because its evidence arrives on
   * both: step one is the name tier at any company, step two adds the email and
   * phone to the same candidates (decision 2210). A match at step two offers the
   * existing employment rather than failing the save.
   */
  const check = useCallback(
    async (which: 1 | 2) => {
      const answer = await post<{ candidates: DuplicateCandidate[]; hits?: DuplicateHit[]; existingEmploymentId?: string | null }>(
        `${PAGES_PATH}/employment-modal/check`,
        { step: which, personId, firstName, lastName, companyId, workEmail, workPhone, title },
        apiBaseUrl,
      );
      if (!answer.ok) return;
      setCandidates(answer.data.candidates ?? []);
      const found = answer.data.hits ?? [];
      setHits(found);
      // 🔨 A name match is a question, and the question is asked rather than
      // shown as a note somebody scrolls past. It opens on a match the person
      // has not already answered, and never when they picked an existing person.
      const name = `${firstName}\u0000${lastName}`.toLowerCase();
      if (askedAbout.current.name !== name) askedAbout.current = { name, ids: new Set() };
      const fresh = found.filter((hit) => !askedAbout.current.ids.has(hit.id));
      if (fresh.length > 0 && !personId) {
        for (const hit of found) askedAbout.current.ids.add(hit.id);
        setAsking(true);
      }
      setExistingEmploymentId(answer.data.existingEmploymentId ?? null);
    },
    [apiBaseUrl, companyId, firstName, lastName, personId, title, workEmail, workPhone],
  );

  const next = useCallback(() => {
    if (origin === 'person' && !companyId) return;
    void check(1);
    setStep(2);
  }, [check, companyId, origin]);

  /**
   * 🔨 The considered answer: choosing *this person* creates no new
   * `PersonRecord`, only a new `EmploymentRecord` to the existing one
   * (decision 0042). The name fields go with the id it locks to.
   */
  const useExisting = useCallback((hit: DuplicateHit) => {
    setPickedPersonId(hit.id);
    /**
     * 💰 The name came from splitting the label on its first space, so Ana
     * Maria Lopez became Ana with a surname of Maria Lopez, and a name written
     * with a middle initial fared no better (found in review, 2026-09-15). The
     * hit carries the record's own two fields, and the split is the fallback
     * for a hit that predates them.
     */
    const [first = '', ...rest] = hit.label.split(' ');
    setFirstName(hit.firstName ?? first);
    setLastName(hit.lastName ?? rest.join(' '));
    setHits([]);
    setAsking(false);
  }, []);

  const save = useCallback(async () => {
    setError(null);
    const answer = await post<{ person: { id: string }; employment: { id: string } }>(
      `${PAGES_PATH}/employment-modal`,
      { origin, personId, firstName, lastName, companyId, title, workEmail, workPhone, workPhoneExtension: extension },
      apiBaseUrl,
    );
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onCreated({ personId: answer.data.person.id, employmentId: answer.data.employment.id, personName: `${firstName} ${lastName}`.trim() || personName || '' });
    onCancel();
  }, [apiBaseUrl, companyId, extension, firstName, lastName, onCancel, onCreated, origin, personId, personName, title, workEmail, workPhone]);

  /** One position's title or day changed in the list. */
  const setLine = useCallback((key: string, change: Partial<EmploymentPositionDraft>) => {
    setLines((held) => held.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }, []);

  /**
   * 🔒 + Add Position (decision 6660): a row after the latest. On an open job it
   * starts today and the latest it follows ends the day before, where it names
   * no last day yet (a latest that begins today or later ends on its first day,
   * and the new row starts the day after); on an ended job its days are the person's to type.
   */
  const addPosition = useCallback(() => {
    added.current += 1;
    const key = `added-${added.current}`;
    setLines((held) => {
      const ordered = positionsOldestFirst(held);
      const latest = ordered[ordered.length - 1] ?? null;
      if (alreadyEnded) return [...ordered, { key, id: null, version: null, title: '', startDate: '', endDate: latest?.endDate ?? null }];
      // A latest that begins today or later cannot end the day before today; it ends on its own first day and the new row starts the day after (T2982).
      const beginsLate = latest !== null && !latest.endDate && latest.startDate >= today;
      const closed = ordered.map((line) => {
        if (line !== latest || line.endDate) return line;
        return line.startDate < today ? { ...line, endDate: positionDayBefore(today) } : { ...line, endDate: line.startDate };
      });
      return [...closed, { key, id: null, version: null, title: '', startDate: beginsLate ? addCalendarDays(latest.startDate, 1) : today, endDate: null }];
    });
  }, [alreadyEnded, today]);

  /**
   * 🔒 A remove per position; the last one left is refused in words, because an employment holds one at least.
   * 🔨 On an open job the title left latest is open again (T3025, decision 7140): it draws Present, so a last day + Add Position gave it has no field left to clear.
   */
  const removePosition = useCallback((key: string) => {
    if (lines.length <= 1) {
      setError(positionListProblem([], { open: !alreadyEnded }));
      return;
    }
    setLines((held) => {
      const kept = positionsOldestFirst(held.filter((line) => line.key !== key));
      if (alreadyEnded) return kept;
      return kept.map((line, index) => (index === kept.length - 1 ? { ...line, endDate: null } : line));
    });
  }, [alreadyEnded, lines.length]);

  /**
   * The correction in place (decisions 0589, 6660): the positions list first,
   * validated as one span set here and on the server and written whole, then
   * the job's work email and work phone where they changed, through the
   * employment's own edit with the latest title. 💰 Cleared means ended, never
   * deleted: which number we called in March must stay answerable (decision 0170).
   */
  const saveEdit = useCallback(async () => {
    if (!editing) return;
    setError(null);
    const ordered = positionsOldestFirst(lines);
    const problem = positionListProblem(ordered, { open: !alreadyEnded });
    if (problem) {
      setError(problem);
      return;
    }
    if (positionsChanged(opened, ordered)) {
      const sent = await put(employmentPositionsPath(editing.employmentId), { positions: ordered.map(({ key: _key, ...row }) => row) }, apiBaseUrl);
      if (!sent.ok) {
        setError(sent.message);
        return;
      }
    }
    // 🔨 An ended employment's lines are history and the server refuses them, so the fields are not shown and not sent.
    const linesChanged = !alreadyEnded && (workEmail !== (editing.workEmail ?? '') || workPhone !== (editing.workPhone ?? '') || extension !== (editing.workPhoneExtension ?? ''));
    if (linesChanged) {
      const answer = await patch(
        `${PAGES_PATH}/employments/${editing.employmentId}`,
        { title: ordered[ordered.length - 1]!.title.trim(), workEmail, workPhone, workPhoneExtension: extension },
        apiBaseUrl,
      );
      if (!answer.ok) {
        setError(answer.message);
        return;
      }
    }
    onCreated({ personId: personId ?? '', employmentId: editing.employmentId });
    onCancel();
  }, [alreadyEnded, apiBaseUrl, editing, extension, lines, onCancel, onCreated, opened, personId, workEmail, workPhone]);

  /**
   * End employment, from inside the edit (decision 0589): the last day above and
   * the one end path, which is what deactivates the login (decision 0165). The
   * confirmation the Destructive button opens says both before anything is
   * posted, which is what the row's own End could never do.
   */
  const endEmployment = useCallback(async () => {
    if (!editing) return;
    setError(null);
    // 🔒 Decision 11740: the successor named for each department, keyed by its id or `all`, which the end writes as the hand-off.
    // 🔒 Decision 11945: End is never held; an empty box names nobody and the chain's automatic hand-off takes that department.
    const named = Object.fromEntries(Object.entries(successors).filter(([, value]) => value !== ''));
    const answer = await post(`${PAGES_PATH}/employments/${editing.employmentId}/end`, { endDate: lastDay, ...(Object.keys(named).length > 0 ? { approvalSuccessors: named } : {}) }, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onCreated({ personId: personId ?? '', employmentId: editing.employmentId });
    onCancel();
  }, [apiBaseUrl, editing, lastDay, onCancel, onCreated, personId, successors]);

  const lockedHalf = editing ? (editing.otherEnd ?? t('employmentModal.thisCompany')) : origin === 'company' ? (companyName ?? t('employmentModal.thisCompany')) : (personName ?? t('employmentModal.thisPerson'));
  const stepOneTitle = origin === 'company' ? t('employmentModal.stepOne') : t('employmentModal.stepOnePerson');

  const shownHits = open ? hits : [];
  const askingNow = open && asking && shownHits.length > 0;

  return (
    <>
    {askingNow ? (
      <DuplicateModal
        open
        kind="person"
        subject={`${firstName} ${lastName}`.trim() || (personName ?? '')}
        subjectDetail={workEmail || null}
        hits={shownHits}
        onKeepBoth={() => setAsking(false)}
        onUseExisting={useExisting}
        onCancel={() => setAsking(false)}
      />
    ) : null}
    <Modal
      open={open}
      title={editing ? t('employmentModal.editTitle') : step === 1 ? stepOneTitle : t('employmentModal.stepTwo')}
      subtitle={editing ? t('employmentModal.editSubtitle') : lockedHalf}
      onCancel={onCancel}
      openerId={openerId}
      dataModal={editing ? 'edit-employment' : 'employment'}
      actions={
        editing
          ? [
              { kind: 'primary', label: t('employmentModal.saveEdit'), onAct: () => void saveEdit(), disabled: lines.some((line) => line.title.trim() === '') },
              { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
            ]
          : step === 1
          ? [
              { kind: 'primary', label: t('employmentModal.next'), onAct: next, disabled: origin === 'person' && !companyId },
              { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
            ]
          : [
              { kind: 'primary', label: t('modal.save'), onAct: () => save() },
              { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
            ]
      }
    >
      <div data-employment-step={editing ? 'edit' : step}>
        {/* 🔨 A banner that states a problem offers an action; dismissing is the
            one this modal has, because the refusal is answered by editing the
            fields behind it (piece 3). */}
        {shownError && <AlertBanner tone="error" message={shownError} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        {shownCandidates.length > 0 && (
          <AlertBanner
            tone="warning"
            message={`${t('employmentModal.duplicates')} ${shownCandidates.map((match) => `${match.candidate.firstName ?? ''} ${match.candidate.lastName ?? ''} (${match.tier})`).join(', ')}`}
            action={{ label: t('modal.dismiss'), onAction: () => setCandidates([]) }}
          />
        )}
        {shownExisting && <AlertBanner tone="inactive" message={t('employmentModal.alreadyHere')} />}
        {editing ? (
          <>
            {/* 🔒 Decisions 6446 and 6660: the positions, oldest first, each its title and its two days, edited as one list. */}
            <div data-employment-positions="true">
              <p className={styles.positionsLead}>{t('employmentModal.positions')}</p>
              <ol className={styles.positionsList}>
                {lines.map((line, index) => {
                  const latest = index === lines.length - 1;
                  return (
                    <li key={line.key} className={styles.positionRow} data-employment-position={line.id ?? line.key}>
                      <TextInput size={Size.Medium} label={t('employmentModal.title')} value={line.title} onChange={(next) => setLine(line.key, { title: next })} required name={`positionTitle-${index}`} />
                      <DatePicker size={Size.Small} label={t('employmentModal.startDate')} value={line.startDate} onChange={(next) => setLine(line.key, { startDate: next })} mode="date" name={`positionStart-${index}`} required />
                      {latest && !alreadyEnded ? (
                        <p className={styles.positionPresent} data-employment-position-present="true">{t('employmentModal.present')}</p>
                      ) : (
                        <DatePicker size={Size.Small} label={t('employmentModal.endDate')} value={line.endDate ?? ''} onChange={(next) => setLine(line.key, { endDate: next === '' ? null : next })} mode="date" name={`positionEnd-${index}`} />
                      )}
                      <Button size={Size.Small} text={[t('employmentModal.removePosition')]} actions={[() => removePosition(line.key)]} />
                    </li>
                  );
                })}
              </ol>
              <div className={styles.actions}>
                <Button size={Size.Small} text={[t('employmentModal.addPosition')]} actions={[addPosition]} />
              </div>
              <p className={modalStyles.readOnly}>{alreadyEnded ? t('employmentModal.endDateNote') : t('employmentModal.positionsNote')}</p>
            </div>
            {alreadyEnded ? null : (
              <>
                <TextInput size={Size.Medium} label={t('employmentModal.workEmail')} value={workEmail} onChange={setWorkEmail} type="email" />
                {/* 🔨 The extension is its own field: a dialer needs it apart from the number (decision 0340). */}
                <PhoneInput size={Size.Medium} label={t('employmentModal.workPhone')} value={workPhone} onChange={setWorkPhone} extension={extension} onExtensionChange={setExtension} name="workPhone" extensionName="workPhoneExtension" />
                <p className={modalStyles.readOnly} data-employment-cleared-note="true">{t('employmentModal.clearedNote')}</p>
              </>
            )}
            {alreadyEnded ? null : (
              /* 🔨 The End is in the Body, not the Footer: the Footer holds one destructive
                 secondary and this one needs the day beside it to make sense (piece 5). */
              <div data-employment-end-block="true">
                <DatePicker size={Size.Small} label={t('employmentModal.endLead')} value={lastDay} onChange={setLastDay} mode="date" name="employmentLastDay" />
                <p className={modalStyles.readOnly}>{t('employmentModal.endHint')}</p>
                {handOff.state.kind === 'loading' ? (
                  <p className={modalStyles.readOnly} role="status" data-employment-handoff-state="loading">{t('employmentModal.handOffLoading')}</p>
                ) : null}
                {handOff.state.kind === 'failed' ? (
                  <p className={modalStyles.readOnly} role="alert" data-employment-handoff-state="failed">{fill(t('employmentModal.handOffFailed'), { reason: handOff.state.error })}</p>
                ) : null}
                {handOffPlan && handOffPlan.departments.length > 0 ? (
                  <div data-employment-handoff={handOffPlan.departments.length}>
                    <p className={styles.positionsLead}>{t('employmentModal.handOffLead')}</p>
                    <p className={modalStyles.readOnly}>{t('employmentModal.handOffHint')}</p>
                    {handOffPlan.departments.map((row) => (
                      <Select
                        key={row.key}
                        size={Size.Large}
                        label={fill(t('employmentModal.handOffFor'), { department: row.departmentName ?? t('employmentModal.handOffAll') })}
                        value={successors[row.key] ?? ''}
                        onChange={(next) => setSuccessorPicks((held) => ({ ...held, [row.key]: next }))}
                        placeholder={t('employmentModal.handOffPick')}
                        options={handOffPlan.people.map((one) => ({ value: one.employmentId, label: one.name }))}
                        dataAttribute={{ 'data-employment-successor': row.key }}
                      />
                    ))}
                    <p className={modalStyles.readOnly} data-employment-handoff-empty="true">{t('employmentModal.handOffEmpty')}</p>
                  </div>
                ) : null}
                <div className={styles.actions}>
                  <Button
                    size={Size.Small}
                    color={Color.Destructive}
                    text={[t('employmentModal.end')]}
                    actions={[() => void endEmployment()]}
                    disabled={lastDay === '' || handOff.state.kind === 'loading'}
                    {...(handOff.state.kind === 'loading' ? { disabledReason: t('employmentModal.handOffLoading') } : {})}
                    confirm={{ title: t('employmentModal.endTitle'), consequence: { body: t('employmentModal.endConsequence') }, confirmLabel: t('employmentModal.endConfirm') }}
                  />
                </div>
              </div>
            )}
          </>
        ) : step === 1 && origin === 'person' ? (
          <>
            {companyHint ? <p className={modalStyles.readOnly} data-employment-company-hint="true">{fill(t('recordChecks.employerHint'), { name: companyHint })}</p> : null}
            <Select size={Size.Medium}
              label={t('employmentModal.company')}
              required
              value={pickedOrHinted ?? ''}
              onChange={(next) => setPickedCompanyId(next === '' ? null : next)}
              placeholder={t('employmentModal.pickCompany')}
              options={(companies.state.kind === 'ready' ? companies.state.data : []).map((row) => ({
                value: row.id,
                label: row.selectable ? row.displayName : `${row.displayName} · ${row.reason ?? ''}`,
                disabled: !row.selectable,
              }))}
              dataAttribute={{ 'data-employment-field': 'company' }}
            />
            {/* 🔨 Our own Company is never silently filtered out: the row says so and links to the Hire window, where its jobs are made (decision 0165; decisions 2690 and 3181). */}
            {ownCompany ? (
              <p className={modalStyles.readOnly} data-employment-hire-note="true">
                {t('employmentModal.hireInstead')} <Link to={ownCompany.hireRoute ?? HIRE_WINDOW_ROUTE} data-employment-hire-link="true">{t('employmentModal.hireLink')}</Link>
              </p>
            ) : null}
          </>
        ) : step === 1 ? (
          <>
            {/* Step one writes the name-only PersonRecord; email, phone and company belong to the employment. */}
            <TextInput size={Size.Medium} label={t('employmentModal.firstName')} value={firstName} onChange={setFirstName} onBlur={() => void check(1)} required />
            <TextInput size={Size.Medium} label={t('employmentModal.lastName')} value={lastName} onChange={setLastName} onBlur={() => void check(1)} required />
          </>
        ) : (
          <>
            {/* 🔨 The Footer holds a Primary, a Cancel and at most one destructive
                secondary (piece 5), so stepping back is a Body control. */}
            <Button size={Size.Small} text={[t('employmentModal.back')]} actions={[() => setStep(1)]} />
            {/* A Position is written for every Employment, so step two always collects a title (decision 0147). */}
            <TextInput size={Size.Large} label={t('employmentModal.title')} value={title} onChange={setTitle} required />
            <TextInput size={Size.Medium} label={t('employmentModal.workEmail')} value={workEmail} onChange={setWorkEmail} onBlur={() => void check(2)} type="email" />
            {/* 🔨 The extension is its own field: a dialer needs it apart from the number (decision 0340). */}
            <PhoneInput size={Size.Medium} label={t('employmentModal.workPhone')} value={workPhone} onChange={setWorkPhone} onBlur={() => void check(2)} extension={extension} onExtensionChange={setExtension} name="workPhone" extensionName="workPhoneExtension" />
          </>
        )}
      </div>
    </Modal>
    </>
  );
}
